"use client";

import { useLayoutEffect, useRef } from "react";
import Link from "next/link";
import { gsap } from "gsap";
import { splitLinesIntoSpans, revealTextTimeline } from "@/lib/animations";
import { ProgressIndicator } from "./ProgressIndicator";
import type { Collection } from "@/types";

interface HeroContentProps {
  active: Collection;
  activeIndex: number;
  duration: number;
}

export function HeroContent({
  active,
  activeIndex,
  duration,
}: HeroContentProps) {
  const locationRef = useRef<HTMLParagraphElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const isFirst = useRef(true);

  // useLayoutEffect (not useEffect) so if anything DOES need to change,
  // it happens before the browser paints — never after.
  useLayoutEffect(() => {
    if (!headingRef.current || !descRef.current) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // First mount: the JSX already rendered the real title/description,
    // fully visible, in their final position. This is what removes the
    // blink — there is nothing to build or reveal, so we touch nothing
    // and the first thing the user ever sees is the finished state.
    if (isFirst.current) {
      isFirst.current = false;
      if (locationRef.current) gsap.set(locationRef.current, { opacity: 1, y: 0 });
      if (buttonRef.current) gsap.set(buttonRef.current, { opacity: 1, y: 0 });
      return;
    }

    // Every subsequent call is a real slide change — this is where the
    // reveal animation belongs, not on load.
    tlRef.current?.kill();

    if (!reduced) {
      gsap.to(buttonRef.current, { opacity: 0, y: -8, duration: 0.2 });
    }

    const titleSpans = splitLinesIntoSpans(headingRef.current, active.title);

    descRef.current.innerHTML = `<span class="block">${active.description}</span>`;
    const descSpan = descRef.current.querySelector("span") as HTMLElement | null;

    tlRef.current = revealTextTimeline({
      locationEl: locationRef.current,
      titleSpans,
      descSpan,
      reduced,
    });

    if (!reduced && buttonRef.current) {
      gsap.set(buttonRef.current, { opacity: 0, y: 10 });
      tlRef.current.to(
        buttonRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        "-=0.3"
      );
    } else if (buttonRef.current) {
      gsap.set(buttonRef.current, { opacity: 1, y: 0 });
    }

    return () => {
      tlRef.current?.kill();
    };
  }, [active]);

  return (
    <div className="max-w-[640px] flex-[1_1_380px] min-w-[280px]">
      {/* Location — real text, visible immediately (no opacity-0 on mount) */}
      <p
        ref={locationRef}
        className="mb-4 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[3px] text-amber"
      >
        <span className="inline-block h-px w-5 bg-amber" />
        {active.location}
      </p>

      {/* Heading — rendered as real lines up front, mirroring the exact
          block/inline-span structure splitLinesIntoSpans would build, so
          layout never shifts when a later slide change swaps this out. */}
      <h1
        ref={headingRef}
        className="text-[clamp(2.6rem,6vw,5.5rem)] font-extrabold uppercase leading-[0.92] tracking-tight text-paper"
      >
        {active.title.map((line, i) => (
          <span className="block overflow-hidden" key={i}>
            <span className="inline-block">{line}</span>
          </span>
        ))}
      </h1>

      {/* Description — real text immediately */}
      <p
        ref={descRef}
        className="mt-5 max-w-[420px] text-[15px] leading-relaxed text-paper/65"
      >
        <span className="block">{active.description}</span>
      </p>

      {/* CTA — visible immediately, no opacity-0 on mount */}
      <Link
        ref={buttonRef}
        href={active.button.href}
        className="mt-7 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[2.5px] text-amber underline-offset-4 hover:underline transition-colors duration-200"
      >
        {active.button.label}
        <span aria-hidden="true">→</span>
      </Link>

      <ProgressIndicator activeIndex={activeIndex} duration={duration} />
    </div>
  );
}